import React, { useState, useEffect } from 'react';
import { Loader2, Calculator } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LoadingOverlayProps {
  duration?: number;
  message?: string;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message, duration = 6000 }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<number>(0);
  
  useEffect(() => {
    console.log("LoadingOverlay mounted with duration:", duration);
    // Start time for the animation
    let start: number | null = null;
    // Animation frame ID for cleanup
    let animationFrameId: number;

    // Animation function that gets called on each frame
    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      
      // Calculate progress based on elapsed time
      const elapsed = Math.max(0, timestamp - start);
      const progressPercent = Math.min((elapsed / duration) * 100, 100);
      
      // Log progress updates periodically (not every frame to avoid console spam)
      if (Math.round(progressPercent) % 10 === 0 || progressPercent >= 99) {
        console.log(`Progress bar: ${progressPercent.toFixed(1)}% (${elapsed.toFixed(0)}ms / ${duration}ms) - width set to ${progressPercent}%`);
      }
      
      // Update state
      // Use a callback to ensure we're always using the latest value
      setProgress(() => progressPercent);
      
      // Continue animation if not complete
      if (progressPercent < 100) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        console.log("Progress animation completed at 100%!");
      }
    };
    
    // Start the animation
    animationFrameId = requestAnimationFrame(animate);
    console.log("Animation started with ID:", animationFrameId);
    
    // Cleanup function
    return () => {
      if (animationFrameId) {
        console.log(`Cancelling animation with ID: ${animationFrameId} at progress: ${progress.toFixed(1)}%`);
        cancelAnimationFrame(animationFrameId);
      }
      console.log("LoadingOverlay unmounted");
    };
  }, [duration]); // Removed progress from dependencies to avoid re-running effect
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
        {/* Progress Bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-4 relative">
          <div 
            className="h-full bg-green-500 absolute top-0 left-0"
            style={{ 
              width: `${progress}%`
            }}
          ></div>
          <div className="absolute top-3 left-0 w-full text-center text-xs text-gray-500 flex justify-center">
            <span className="bg-white px-1 rounded">Progreso: {progress.toFixed(0)}%</span>
          </div>
        </div>
        
        <div className="flex flex-col items-center mt-6">
          <div className="rounded-full bg-primary/10 p-3 mb-4">
            <Calculator size={36} className="text-primary" />
          </div>
          
          <Loader2 size={42} className="animate-spin text-primary mb-3" />
          
          <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
            {t('quotationForm.calculating')}
          </h3>
          
          <p className="text-gray-600 text-center text-sm">
            {message || t('loadingOverlay.message', 'Please wait while we calculate all costs, including tolls and taxes.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingOverlay;